import * as React from "react";

import { definedMapWithDefault, definedMap } from "collection-utils";

import { asString, isLoadedDefined, asArrayValue } from "common/components/component-helpers";
import { LOADING, ActionSource } from "common/components/types";
import type { ExpandableOptionValue } from "../ui/smart/expandable-options/expandable-options";
import ExpandableOptions from "../ui/smart/expandable-options/expandable-options";
import { mapFilterUndefined, isEmptyOrUndefined } from "common/Support";
import type { GlideFunctionalRenderer } from "common/components/uis";
import { useForceValueProducers } from "hooks/use-value-producers";
import { useForce } from "hooks/use-force";
import { useOnClickFromAction } from "hooks/use-on-click-from-action";
import { useEditComponentEnabledViaNetworkStatus } from "hooks/use-edit-components-enabled-via-network-status";

export const ReactionMaterialRenderer: GlideFunctionalRenderer = p => {
    const { env, getValueProducers, ctx } = p;
    const [force] = useForce();
    const [[reactionsPV, valuePV, onChangePV, titlePV], [, valueVP]] = useForceValueProducers(
        getValueProducers,
        force,
        ctx
    );
    const { onClick: onChangeCB } = useOnClickFromAction(onChangePV, ActionSource.DateTimeField, ctx, env);
    const [editingAllowed] = useEditComponentEnabledViaNetworkStatus(env);

    const onChange = React.useCallback(
        (newValue: ExpandableOptionValue) => {
            if (valueVP === undefined) return;

            valueVP.set(newValue ?? "");
            if (onChangeCB !== undefined) {
                void onChangeCB(undefined);
            }
        },
        [valueVP, onChangeCB]
    );

    if (titlePV === LOADING || valuePV === LOADING || onChangePV === LOADING || reactionsPV === LOADING) {
        return env.renderProgress();
    }

    const value = definedMapWithDefault(valuePV, "", asString);
    const title = definedMapWithDefault(titlePV, "", asString);

    const expandableOptions = mapFilterUndefined(asArrayValue(reactionsPV), vp => {
        const inspector = vp.getObjectInspector(force);
        if (!isLoadedDefined(inspector)) return undefined;

        const emoji = definedMap(inspector.getProperty("emoji"), force);
        const reactionText = definedMap(inspector.getProperty("value"), force);

        if (!isLoadedDefined(emoji) || !isLoadedDefined(value)) return undefined;

        return { value: asString(reactionText), label: asString(reactionText), icon: asString(emoji) };
    });

    // Avoid rendering the reaction option if the user has not set a value
    const expandableOptionsFiltered = expandableOptions.filter(eo => !isEmptyOrUndefined(eo.value));
    if (expandableOptionsFiltered.length === 0) return null;

    const isEnabled = editingAllowed;

    return (
        <ExpandableOptions
            isEnabled={isEnabled}
            value={asString(value)}
            caption={title}
            onChange={onChange}
            isSvg={false}
            defaultIcon={expandableOptionsFiltered[0].icon}
            options={expandableOptionsFiltered}
        />
    );
};
